import {
  DatePicker,
  FormField,
  NumberStepper,
  SegmentedControl,
  Select,
  Textarea,
  WeightInput,
  todayIso,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { formErrors } from '../../../lib/errors';
import { useRecordCorrection, type Batch, type CorrectionReason } from '../api';
import { formatKg } from '../format';
import styles from './harvest.module.css';

const REASONS: CorrectionReason[] = [
  'shrinkage',
  'spoilage',
  'own_use',
  'feed',
  'counting_error',
  'breakage',
  'gift',
  'inventory',
  'other',
];

export interface CorrectionDialogProps {
  open: boolean;
  onClose: () => void;
  batch: Batch;
}

/** Corrects bulk honey or jars of a batch with a reason (INV-002, INV-022). */
export function CorrectionDialog({ open, onClose, batch }: CorrectionDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const record = useRecordCorrection();
  const [target, setTarget] = useState('bulk');
  const [direction, setDirection] = useState<'out' | 'in'>('out');
  const [amount, setAmount] = useState<number | null>(null);
  const [reason, setReason] = useState<CorrectionReason>('shrinkage');
  const [on, setOn] = useState(todayIso());
  const [note, setNote] = useState('');
  const bulk = target === 'bulk';
  const article = batch.articles.find((a) => a.article.id === target);
  const errors = formErrors(record.error, {
    amount: ['correction.zero', 'stock.'],
    reason: ['correction.reason'],
  });

  const close = () => {
    record.reset();
    onClose();
  };
  const submit = () => {
    const value = amount ?? 0;
    record.mutate(
      {
        batchId: batch.id,
        body: {
          article_id: bulk ? null : target,
          delta: direction === 'out' ? -value : value,
          reason,
          booked_on: on,
          note: note || null,
        },
      },
      {
        onSuccess: () => {
          toast.show(t('harvest.correction.recorded'));
          close();
        },
      },
    );
  };

  return (
    <FormDialog
      open={open}
      onClose={close}
      title={t('harvest.correction.title')}
      description={t('harvest.correction.description', { lot: batch.lot })}
      submitLabel={t('harvest.correction.submit')}
      onSubmit={submit}
      pending={record.isPending}
      error={errors.general}
    >
      <FormField label={t('harvest.correction.target')}>
        {(p) => (
          <Select
            {...p}
            value={target}
            onChange={(e) => {
              setTarget(e.target.value);
              setAmount(null);
            }}
            options={[
              {
                value: 'bulk',
                label: t('harvest.correction.bulk', { amount: formatKg(batch.bulk_grams) }),
              },
              ...batch.articles.map((a) => ({
                value: a.article.id,
                label: t('harvest.correction.jars', { name: a.article.name, count: a.jars }),
              })),
            ]}
          />
        )}
      </FormField>
      <SegmentedControl
        label={t('harvest.correction.direction')}
        value={direction}
        onChange={setDirection}
        fullWidth
        options={[
          { value: 'out', label: t('harvest.correction.out') },
          { value: 'in', label: t('harvest.correction.in') },
        ]}
      />
      <div className={styles.row}>
        <FormField
          label={bulk ? t('harvest.correction.amountKg') : t('harvest.correction.amountJars')}
          required
          error={errors.field.amount}
          hint={article ? t('harvest.correction.inStock', { count: article.jars }) : undefined}
        >
          {(p) =>
            bulk ? (
              <WeightInput
                {...p}
                value={amount}
                onChange={setAmount}
                stepKg={0.1}
                labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
              />
            ) : (
              <NumberStepper
                {...p}
                value={amount}
                onChange={setAmount}
                min={0}
                unit={t('harvest.filling.jarsUnit')}
                labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
              />
            )
          }
        </FormField>
        <FormField label={t('harvest.correction.reason')} required error={errors.field.reason}>
          {(p) => (
            <Select
              {...p}
              value={reason}
              onChange={(e) => setReason(e.target.value as CorrectionReason)}
              options={REASONS.map((r) => ({ value: r, label: t(`harvest.reason.${r}`) }))}
            />
          )}
        </FormField>
      </div>
      <FormField label={t('harvest.correction.on')}>
        {(p) => <DatePicker {...p} value={on} onChange={setOn} max={todayIso()} />}
      </FormField>
      <FormField label={t('harvest.correction.note')}>
        {(p) => <Textarea {...p} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />}
      </FormField>
    </FormDialog>
  );
}
